import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  ClaimControllerApi,
  GetAllClaim1DateRangeEnum,
} from "../../../services";
import { ClaimType } from "../../../pages/app/provider/billings/claims/claims-constants";
import {
  DGT_Pagination,
  getConfigObject,
  getPageIndexWhileSearch,
  getXtenantId,
  useBaseContent,
} from "../../../utils/utils";
import { GridSortItem } from "@mui/x-data-grid";
import { useRef } from "react";

/**
 *
 * @author Atish Bagate
 */
interface Props {
  pagination: DGT_Pagination;
  active?: boolean;
  archive?: boolean;
  status?: boolean;
  patientUuids?: string[];
  providerUuids?: string[];
  gridSorting: GridSortItem[];
  postingStatuses?: string;
  claimStatuses?: string;
  dateRange?: GetAllClaim1DateRangeEnum;
  startDate?: string;
  endDate?: string;
  enabled?: boolean;
  paymentPosted?: boolean;
  searchKey?: string;
  claimType?: ClaimType[];
  claimId?: string;
  scrubStatus?: string;
  payerId?: string;
  /**
   * Claims whose remaining balance the backend should match. The EOB flow uses
   * it so the claim picker only offers claims that still have money owing.
   */
  remainingBalance?: number;
}

export function useGetClaimsV2({
  pagination,
  archive,
  status,
  gridSorting,
  postingStatuses,
  claimStatuses,
  dateRange,
  startDate,
  endDate,
  patientUuids,
  providerUuids,
  enabled = true,
  searchKey,
  paymentPosted,
  claimId,
  claimType,
  scrubStatus,
  payerId,
  remainingBalance,
}: Props): useBaseContent<any> {
  const prevPage = useRef<number>(pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination,
    prevPage,
  });

  const sortData = gridSorting?.[0];

  return useQuery({
    enabled: enabled,
    queryKey: [
      "get-claims-list-v2",
      pagination,
      archive,
      status,
      gridSorting,
      postingStatuses,
      claimStatuses,
      dateRange,
      startDate,
      endDate,
      patientUuids,
      providerUuids,
      searchKey,
      paymentPosted,
      claimId,
      claimType,
      scrubStatus,
      payerId,
      remainingBalance,
    ],
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      new ClaimControllerApi(getConfigObject())
        .getAllClaimV2(
          pageIndex, //page
          pagination.pageSize, //size
          sortData?.field ?? undefined, //sortBy
          sortData?.sort ?? undefined, //sortDirection
          status, //status
          false, //archive
          patientUuids, //patientUuids
          providerUuids, //providerUuids
          claimStatuses, // claimStatuses,
          postingStatuses,
          dateRange, // dateRange,
          startDate, // startDate,
          endDate, // endDate
          undefined, // search by patientName
          scrubStatus,
          claimType,
          claimId,
          paymentPosted, // paymentPosted
          payerId, //payerId
          remainingBalance, //remainingBalance
          searchKey,
          getXtenantId()
        )
        .then((it) => it?.data),
  });
}
